import { useState } from 'react';
import { Input, Button } from '@/components/ui';
import { AddressFields } from './AddressFields';
import { PhoneInputList } from '@/features/customers/components';
import { normalizePhone, isValidPhone } from '@/lib/phoneUtils';
import {
  PROJECT_CUSTOMER_ROLES,
  createDefaultPhoneFormItem,
} from '../types';
import type { ProjectCustomerRole, PhoneFormItem } from '../types';

// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Canadian postal code regex
const POSTAL_CODE_REGEX = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;

// Phone data for submission
export interface PhoneData {
  phone_number: string;
  phone_type: 'mobile' | 'home' | 'work' | 'other';
  label?: string;
  is_primary: boolean;
  can_sms: boolean;
  notes?: string;
}

export interface NewCustomerFormData {
  first_name: string;
  last_name: string;
  email: string;
  phones: PhoneData[];
  billing_street_address: string;
  billing_unit: string | null;
  billing_city: string;
  billing_province: string;
  billing_postal_code: string;
  billing_country: string;
  role: ProjectCustomerRole;
  is_billing: boolean;
}

interface NewCustomerFormProps {
  onSubmit: (data: NewCustomerFormData) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
  error: string | null;
}

export function NewCustomerForm({
  onSubmit,
  onCancel,
  isSubmitting,
  error,
}: NewCustomerFormProps) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phones: [createDefaultPhoneFormItem(true)] as PhoneFormItem[],
    billingStreetAddress: '',
    billingUnit: '',
    billingCity: '',
    billingProvince: 'ON',
    billingPostalCode: '',
    billingCountry: 'Canada',
    role: 'contact' as ProjectCustomerRole,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const { [field]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const handlePhonesChange = (phones: PhoneFormItem[]) => {
    setFormData((prev) => ({ ...prev, phones }));
    // Clear phone errors
    if (errors.phones) {
      setErrors((prev) => {
        const { phones: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!EMAIL_REGEX.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Validate phones - at least warn if invalid format
    const phonesWithNumbers = formData.phones.filter(
      (p) => p.phoneNumber.trim() !== ''
    );
    for (const phone of phonesWithNumbers) {
      if (!isValidPhone(phone.phoneNumber)) {
        newErrors.phones = 'Please enter valid phone numbers (10+ digits)';
        break;
      }
    }

    if (!formData.billingStreetAddress.trim()) {
      newErrors.billingStreetAddress = 'Street address is required';
    }
    if (!formData.billingCity.trim()) {
      newErrors.billingCity = 'City is required';
    }
    if (!formData.billingProvince) {
      newErrors.billingProvince = 'Province is required';
    }
    if (!formData.billingPostalCode.trim()) {
      newErrors.billingPostalCode = 'Postal code is required';
    } else if (!POSTAL_CODE_REGEX.test(formData.billingPostalCode.trim())) {
      newErrors.billingPostalCode =
        'Please enter a valid postal code (e.g., M5V 1A1)';
    }
    if (!formData.billingCountry) {
      newErrors.billingCountry = 'Country is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    // Convert phone form items to phone data
    const phones: PhoneData[] = formData.phones
      .filter((p) => p.phoneNumber.trim() !== '')
      .map((p) => ({
        phone_number: normalizePhone(p.phoneNumber),
        phone_type: p.phoneType,
        label: p.label.trim() || undefined,
        is_primary: p.isPrimary,
        can_sms: p.canSms,
        notes: p.notes.trim() || undefined,
      }));

    await onSubmit({
      first_name: formData.firstName.trim(),
      last_name: formData.lastName.trim(),
      email: formData.email.trim(),
      phones,
      billing_street_address: formData.billingStreetAddress.trim(),
      billing_unit: formData.billingUnit.trim() || null,
      billing_city: formData.billingCity.trim(),
      billing_province: formData.billingProvince,
      billing_postal_code: formData.billingPostalCode.trim().toUpperCase(),
      billing_country: formData.billingCountry,
      role: formData.role,
      is_billing: formData.role === 'billing',
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Error Alert */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* First Name and Last Name */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input
          label="First Name"
          name="firstName"
          value={formData.firstName}
          onChange={(e) => handleChange('firstName', e.target.value)}
          placeholder="John"
          error={errors.firstName}
          disabled={isSubmitting}
          required
        />
        <Input
          label="Last Name"
          name="lastName"
          value={formData.lastName}
          onChange={(e) => handleChange('lastName', e.target.value)}
          placeholder="Smith"
          error={errors.lastName}
          disabled={isSubmitting}
          required
        />
      </div>

      {/* Email */}
      <Input
        label="Email"
        name="email"
        type="email"
        value={formData.email}
        onChange={(e) => handleChange('email', e.target.value)}
        placeholder="john@example.com"
        error={errors.email}
        disabled={isSubmitting}
        required
      />

      {/* Phone Numbers Section */}
      <div className="pt-4 border-t border-secondary-200">
        <h4 className="text-base font-semibold text-secondary-900 mb-4">
          Phone Numbers
        </h4>
        {errors.phones && (
          <p className="text-sm text-red-600 mb-2">{errors.phones}</p>
        )}
        <PhoneInputList
          phones={formData.phones}
          onChange={handlePhonesChange}
          maxPhones={3}
          disabled={isSubmitting}
        />
      </div>

      {/* Billing Address Section */}
      <div className="pt-4 border-t border-secondary-200">
        <h4 className="text-base font-semibold text-secondary-900 mb-4">
          Billing Address
        </h4>
        <AddressFields
          streetAddress={formData.billingStreetAddress}
          unit={formData.billingUnit}
          city={formData.billingCity}
          province={formData.billingProvince}
          postalCode={formData.billingPostalCode}
          country={formData.billingCountry}
          onChange={handleChange}
          errors={{
            streetAddress: errors.billingStreetAddress,
            city: errors.billingCity,
            province: errors.billingProvince,
            postalCode: errors.billingPostalCode,
            country: errors.billingCountry,
          }}
          disabled={isSubmitting}
        />
      </div>

      {/* Project Role Section */}
      <div className="pt-4 border-t border-secondary-200">
        <h4 className="text-base font-semibold text-secondary-900 mb-4">
          Project Role
        </h4>

        {/* Role Select */}
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-1">
            Role
          </label>
          <select
            value={formData.role}
            onChange={(e) => handleChange('role', e.target.value)}
            className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            disabled={isSubmitting}
          >
            {PROJECT_CUSTOMER_ROLES.filter((r) => r.value !== 'primary').map((role) => (
              <option key={role.value} value={role.value}>
                {role.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 pt-3 border-t border-secondary-200">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          size="sm"
          isLoading={isSubmitting}
          disabled={isSubmitting}
        >
          Create & Add
        </Button>
      </div>
    </form>
  );
}
